// Archivo: src/components/SubSectionDisplay.js

import React, { useState, useEffect, useCallback, memo } from "react";
import ReactDOM from "react-dom";
import "./RichTextContent.css";

// --- Iconos Reutilizables (Memoizados para optimización) ---
const PlayIcon = memo(() => (
  <svg
    className="w-12 h-12 text-white drop-shadow-lg"
    fill="currentColor"
    viewBox="0 0 20 20"
  >
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
      clipRule="evenodd"
    />
  </svg>
));
const DocumentIcon = memo(() => (
  <svg
    className="w-8 h-8 text-gob-rojo"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
));
const ViewIcon = memo(() => (
  <svg
    className="w-6 h-6 text-gray-400 group-hover:text-gob-azul-medio transition-colors"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
));

// --- Componente de Cabecera de Sección (Memoizado) ---
const SectionHeader = memo(({ title, color }) => (
  <div className="text-center mb-8">
    <h3 className={`text-3xl font-bold text-gob-azul-oscuro mb-3`}>{title}</h3>
    <div className={`h-1 w-24 mx-auto rounded-full ${color}`}></div>
  </div>
));

// --- Helper para obtener ID de video de YouTube ---
const getYoutubeVideoId = (url) => {
  try {
    const urlObj = new URL(url);
    if (urlObj.hostname.includes("youtube.com"))
      return urlObj.searchParams.get("v");
    if (urlObj.hostname.includes("youtu.be")) return urlObj.pathname.slice(1);
    return null;
  } catch {
    return null;
  }
};

// --- Componente para la Miniatura de Video (Memoizado) ---
const VideoThumbnail = memo(({ video, onPlay }) => {
  const videoId = getYoutubeVideoId(video.url);
  if (!videoId) return null; // No renderizar si la URL no es válida
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  return (
    <div className="w-full max-w-sm mx-auto">
      <div
        className="w-full cursor-pointer group"
        onClick={() => onPlay(video.id)}
      >
        <div className="relative aspect-[16/9] rounded-2xl overflow-hidden shadow-lg border-2 border-transparent group-hover:border-gob-azul-medio transition-all duration-300 transform group-hover:scale-105">
          <img
            src={thumbnailUrl}
            alt={video.title || "Miniatura de video"}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <PlayIcon />
          </div>
        </div>
      </div>
    </div>
  );
});

const STORAGE_URL = process.env.REACT_APP_STORAGE_URL;

// --- Componente para el Item de Documento (Memoizado) ---
const DocumentItem = memo(({ doc }) => (
  <div className="group flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-lg hover:border-gob-azul-medio transition-all duration-300 transform hover:-translate-y-1">
    <a
      href={`${STORAGE_URL}/${doc.path}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-4 min-w-0 flex-grow"
    >
      <DocumentIcon />
      <p className="font-bold text-gob-azul-oscuro truncate group-hover:text-gob-azul-medio transition-colors">
        {doc.title}
      </p>
    </a>
    <a
      href={`${STORAGE_URL}/${doc.path}`}
      target="_blank"
      rel="noopener noreferrer"
      className="p-2 rounded-full hover:bg-gray-100 transition-colors ml-4 flex-shrink-0"
      aria-label="Visualizar documento"
    >
      <ViewIcon />
    </a>
  </div>
));

// --- Componente para el Modal de Video ---
const VideoModal = ({ video, onClose }) => {
  const getYoutubeEmbedUrl = (url) => {
    const videoId = getYoutubeVideoId(url);
    return videoId
      ? `https://www.youtube.com/embed/${videoId}?autoplay=1`
      : url;
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4 animate-fade-in-up"
      onClick={onClose}
    >
      <div
        className="relative bg-black w-full max-w-4xl rounded-lg overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-2 -right-2 text-white bg-gray-800 rounded-full p-2 hover:bg-red-600 transition-colors z-10"
          aria-label="Cerrar video"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </button>
        <div className="aspect-[16/9]">
          <iframe
            src={getYoutubeEmbedUrl(video.url)}
            title={video.title || `Video ${video.id}`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          ></iframe>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// --- Componente Principal ---
export default function SubSectionDisplay({ subsection }) {
  const [playingVideoId, setPlayingVideoId] = useState(null);

  const handlePlayVideo = useCallback((id) => {
    setPlayingVideoId(id);
  }, []);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) setPlayingVideoId(null);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  const selectedVideo = playingVideoId
    ? subsection?.youtube_links.find((v) => v.id === playingVideoId)
    : null;

  useEffect(() => {
    document.body.style.overflow = selectedVideo ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [selectedVideo]);

  if (!subsection) return null;

  const videoCount = subsection.youtube_links?.length || 0;
  const docCount = subsection.documents?.length || 0;

  return (
    <>
      <div id={`subsection-${subsection.id}`} className="scroll-mt-24 mb-16">
        <div className="bg-gradient-to-b from-gray-50 via-white to-gray-50 p-6 md:p-8 rounded-2xl shadow-lg border-2 border-gray-200 transition-all duration-300">
          <header className="text-center mb-12">
            <h2 className="text-4xl font-extrabold text-gob-azul-oscuro mb-6 tracking-tight">
              {subsection.title}
            </h2>
            <div
              className="text-lg text-gray-500 text-left mx-auto max-w-3xl rich-text-content"
              dangerouslySetInnerHTML={{
                __html: subsection.description || "",
              }}
            />
          </header>

          {videoCount > 0 && (
            <section className={docCount > 0 ? "mb-12" : ""}>
              <SectionHeader title="Videos" color="bg-gob-azul-medio" />
              <div className="flex justify-center">
                <div
                  className={`grid gap-8 ${videoCount === 1 ? "grid-cols-1" : videoCount === 2 ? "grid-cols-1 md:grid-cols-2 justify-items-center" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}
                >
                  {subsection.youtube_links.map((video) => (
                    <VideoThumbnail
                      key={video.id}
                      video={video}
                      onPlay={handlePlayVideo}
                    />
                  ))}
                </div>
              </div>
            </section>
          )}

          {docCount > 0 && (
            <section>
              <SectionHeader title="Documentos" color="bg-gob-rojo" />
              <div className="space-y-4 max-w-3xl mx-auto">
                {subsection.documents.map((doc) => (
                  <DocumentItem key={doc.id} doc={doc} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>

      {selectedVideo && (
        <VideoModal
          video={selectedVideo}
          onClose={() => setPlayingVideoId(null)}
        />
      )}
    </>
  );
}
